import { get, post } from "./ajax";
import Swal from "sweetalert2-neutral";

/**
 * Configuration constants for EPG Manager
 */
const EPG_CONFIG = {
    CHANNELS_PER_PAGE: 10,
    TIME_SLOT_DURATION: 30, // minutes
    TIME_SLOTS_COUNT: 48, // 24 hours
    SLOT_WIDTH: 250, // pixels
    CHANNEL_WIDTH: 200, // pixels
    DATE_RANGE_DAYS: 3,
    UPDATE_INTERVAL: 60000, // 1 minute
    INFINITE_SCROLL_THRESHOLD: 200, // pixels
    FOCUS_RETRY_COUNT: 3,
    FOCUS_INITIAL_DELAY: 200,
    FOCUS_RETRY_DELAY: 500,
    MAX_CONCURRENT_REQUESTS: 5,
    SCROLL_THROTTLE: 16, // ~60fps
    CACHE_EXPIRY: 5 * 60 * 1000 // 5 minutes
};

/**
 * Utility functions
 */
const Utils = {
    /**
     * Throttle function execution
     */
    throttle(func, delay) {
        let timeoutId;
        let lastExecTime = 0;
        return function (...args) {
            const currentTime = Date.now();

            if (currentTime - lastExecTime > delay) {
                func.apply(this, args);
                lastExecTime = currentTime;
            } else {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    func.apply(this, args);
                    lastExecTime = Date.now();
                }, delay - (currentTime - lastExecTime));
            }
        };
    },

    /**
     * Debounce function execution
     */
    debounce(func, delay) {
        let timeoutId;
        return function (...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(this, args), delay);
        };
    },

    /**
     * Format date for display
     */
    formatDateDisplay(date, offset) {
        if (offset === 0) return 'Today';
        if (offset === -1) return 'Yesterday';
        if (offset === 1) return 'Tomorrow';

        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        return `${day}${this.getOrdinalSuffix(day)} ${month}`;
    },

    /**
     * Get ordinal suffix for day numbers
     */
    getOrdinalSuffix(day) {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    },

    /**
     * Check if cache is valid
     */
    isCacheValid(timestamp) {
        return timestamp && (Date.now() - timestamp) < EPG_CONFIG.CACHE_EXPIRY;
    }
};

/**
 * Enhanced EPGManager class for handling Electronic Program Guide functionality
 */
class EPGManager {
    constructor(containerId, channelId = null) {
        this._validateConstructorParams(containerId);

        // Core properties
        this.containerId = containerId;
        this.channelId = channelId;

        // Data storage with caching
        this.channels = [];
        this.programData = new Map();
        this.programCache = new Map(); // Cache for program data
        this.timeSlots = [];

        // State management
        this.state = {
            currentTime: new Date(),
            selectedDate: new Date().toISOString().split('T')[0],
            userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            isLoading: false,
            isLoadingMoreChannels: false,
            timeIndicatorVisible: true,
            initialized: false
        };

        // Pagination configuration - Updated to match API response
        this.pagination = {
            page: 1,
            per_page: EPG_CONFIG.CHANNELS_PER_PAGE,
            total: 0,
            total_pages: 0,
            has_next: false,
            has_prev: false
        };

        // Performance optimization
        this.requestQueue = new Map();
        this.loadingChannels = new Set();

        // Date range for date picker
        this.dateRange = this._generateDateRange();

        // DOM element references
        this.elements = {};

        // Intervals and timeouts
        this.intervals = {
            timeUpdater: null
        };

        // Drag state management
        this.dragState = {
            isDragging: false,
            startX: 0,
            startScrollLeft: 0,
            hasMovedMouse: false,
            dragThreshold: 5,
            velocity: 0,
            lastMoveTime: 0,
            lastMoveX: 0,
            momentumAnimation: null
        };

        // Bound methods for event listeners (throttled for performance)
        this._boundMethods = {
            handleTimelineScroll: Utils.throttle(this._handleTimelineScroll.bind(this), EPG_CONFIG.SCROLL_THROTTLE),
            handleProgramsGridScroll: Utils.throttle(this._handleProgramsGridScroll.bind(this), EPG_CONFIG.SCROLL_THROTTLE),
            handleInfiniteScroll: Utils.throttle(this._handleInfiniteScroll.bind(this), 100),
            handleDatePickerClick: this._handleDatePickerClick.bind(this),
            updateTime: this._updateTime.bind(this),
            handleMouseDown: this._handleMouseDown.bind(this),
            handleMouseMove: this._handleMouseMove.bind(this),
            handleMouseUp: this._handleMouseUp.bind(this),
            handleTouchStart: this._handleTouchStart.bind(this),
            handleTouchMove: this._handleTouchMove.bind(this),
            handleTouchEnd: this._handleTouchEnd.bind(this)
        };

        // Initialize the EPG
        this._initialize();
    }

    /**
     * Validate constructor parameters
     */
    _validateConstructorParams(containerId) {
        if (!containerId || typeof containerId !== 'string') {
            throw new Error('EPGManager: containerId is required and must be a string');
        }

        const containerElement = document.getElementById(containerId);
        if (!containerElement) {
            throw new Error(`EPGManager: Container element with ID '${containerId}' not found in the DOM`);
        }
    }

    /**
     * Initialize the EPG Manager
     */
    async _initialize() {
        try {
            this._createEPGStructure();
            this._setupEventListeners();
            this._assignElementReferences();

            // Parallel initialization
            const [channelsResult] = await Promise.allSettled([
                this._loadChannels(),
                this._generateTimeSlots()
            ]);

            if (channelsResult.status === 'rejected') {
                throw channelsResult.reason;
            }

            // Render skeleton immediately
            this._renderEPG(true);

            // Load program data in batches for better performance
            await this._loadInitialProgramDataBatched();

            this._updateTimeIndicator();
            this._startTimeUpdater();
            this.state.initialized = true;

            // Focus on current time after initialization
            this._focusOnLiveTime();

            // Optionally prefetch data for adjacent dates for better performance
            setTimeout(() => {
                this.prefetchAdjacentDates().catch(error => {
                    // Silent catch - this is an optional performance enhancement
                    console.debug('EPG prefetch failed (not critical):', error);
                });
            }, 2000); // Wait 2 seconds after initial load to avoid overwhelming the server
        } catch (error) {
            console.error('Error initializing EPG Manager:', error);
            throw error;
        }
    }

    /**
     * Generate date range for date picker
     */
    _generateDateRange() {
        const dates = [];
        const today = new Date();
        const rangeDays = Math.floor(EPG_CONFIG.DATE_RANGE_DAYS / 2);

        for (let i = -rangeDays; i <= rangeDays; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);

            dates.push({
                date: date.toISOString().split('T')[0],
                display: Utils.formatDateDisplay(date, i),
                isToday: i === 0,
                dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
                dayNumber: date.getDate(),
                monthName: date.toLocaleDateString('en-US', { month: 'short' })
            });
        }

        return dates;
    }

    /**
     * Create the EPG HTML structure
     */
    _createEPGStructure() {
        const container = document.getElementById(this.containerId);
        const instanceId = Date.now();
        const ids = {
            timelineScroll: `timelineScroll_${instanceId}`,
            programsGrid: `programsGrid_${instanceId}`,
            timeIndicator: `timeIndicator_${instanceId}`,
            channelRows: `channelRows_${instanceId}`,
            datePicker: `datePicker_${instanceId}`
        };

        const epgElement = this._createEPGDOM(ids);
        container.appendChild(epgElement);
        this._storeElementReferences(ids);
    }

    /**
     * Create the EPG DOM structure using DocumentFragment for better performance
     */
    _createEPGDOM(ids) {
        const fragment = document.createDocumentFragment();

        // Create main container
        const epgContainer = document.createElement('div');
        epgContainer.className = 'epg-container';

        // Create timeline header
        const timelineHeader = this._createTimelineHeader(ids);

        // Create EPG content
        const epgContent = this._createEPGContent(ids);

        epgContainer.appendChild(timelineHeader);
        epgContainer.appendChild(epgContent);
        fragment.appendChild(epgContainer);

        return fragment;
    }

    /**
     * Create timeline header section
     */
    _createTimelineHeader(ids) {
        const timelineHeader = document.createElement('div');
        timelineHeader.className = 'timeline-header';

        // Timeline corner
        const timelineCorner = document.createElement('div');
        timelineCorner.className = 'timeline-corner';
        timelineCorner.innerHTML = `
            <div class="corner-content">
                <span class="corner-label">Channel/Day</span>
            </div>
        `;

        // Date-time wrapper
        const dateTimeWrapper = document.createElement('div');
        dateTimeWrapper.className = 'date-time-wrapper';

        // Date picker
        const datePicker = document.createElement('div');
        datePicker.className = 'date-picker';
        datePicker.id = ids.datePicker;
        this._createDatePickerDOM(datePicker);

        // Timeline slots
        const timelineSlots = document.createElement('div');
        timelineSlots.className = 'timeline-slots sync-scroll';
        timelineSlots.id = ids.timelineScroll;

        dateTimeWrapper.appendChild(datePicker);
        dateTimeWrapper.appendChild(timelineSlots);
        timelineHeader.appendChild(timelineCorner);
        timelineHeader.appendChild(dateTimeWrapper);

        return timelineHeader;
    }

    /**
     * Create EPG content section
     */
    _createEPGContent(ids) {
        const epgContent = document.createElement('div');
        epgContent.className = 'epg-content';

        const programsGrid = document.createElement('div');
        programsGrid.className = 'programs-grid sync-scroll';
        programsGrid.id = ids.programsGrid;

        const programsContainer = document.createElement('div');
        programsContainer.className = 'programs-container';

        // Time indicator
        const timeIndicator = document.createElement('div');
        timeIndicator.className = 'time-indicator';
        timeIndicator.id = ids.timeIndicator;
        timeIndicator.innerHTML = '<div class="live-indicator">LIVE</div>';

        // Channel rows
        const channelRows = document.createElement('div');
        channelRows.id = ids.channelRows;

        // Loading indicator
        const loadingMoreChannels = document.createElement('div');
        loadingMoreChannels.className = 'loading-more-channels';
        loadingMoreChannels.style.display = 'none';
        loadingMoreChannels.innerHTML = '<div class="loading-spinner">Loading more channels...</div>';

        programsContainer.appendChild(timeIndicator);
        programsContainer.appendChild(channelRows);
        programsContainer.appendChild(loadingMoreChannels);
        programsGrid.appendChild(programsContainer);
        epgContent.appendChild(programsGrid);

        return epgContent;
    }

    /**
     * Store references to DOM elements
     */
    _storeElementReferences(ids) {
        this.elements = {
            timelineScroll: document.getElementById(ids.timelineScroll),
            programsGrid: document.getElementById(ids.programsGrid),
            timeIndicator: document.getElementById(ids.timeIndicator),
            channelRows: document.getElementById(ids.channelRows),
            datePicker: document.getElementById(ids.datePicker),
            loadingIndicator: document.querySelector('.loading-more-channels')
        };

        // Validate elements
        const missingElements = Object.entries(this.elements)
            .filter(([key, element]) => !element)
            .map(([key]) => key);

        if (missingElements.length > 0) {
            throw new Error(`Failed to create EPG elements: ${missingElements.join(', ')}`);
        }

        // Backward compatibility
        this._assignElementReferences();
    }

    /**
     * Assign element references for backward compatibility
     */
    _assignElementReferences() {
        this.timelineScrollElement = this.elements.timelineScroll;
        this.programsGridElement = this.elements.programsGrid;
        this.timeIndicatorElement = this.elements.timeIndicator;
        this.channelRowsElement = this.elements.channelRows;
        this.datePickerElement = this.elements.datePicker;
        this.loadingIndicator = this.elements.loadingIndicator;
    }

    /**
     * Create date picker DOM elements
     */
    _createDatePickerDOM(container) {
        container.innerHTML = '';

        const fragment = document.createDocumentFragment();

        this.dateRange.forEach(dateObj => {
            const dateItem = document.createElement('div');
            dateItem.className = `date-item ${dateObj.isToday ? 'active' : ''}`;
            dateItem.setAttribute('data-date', dateObj.date);

            const dateDisplay = document.createElement('span');
            dateDisplay.className = 'date-display';
            dateDisplay.textContent = dateObj.display;

            dateItem.appendChild(dateDisplay);
            fragment.appendChild(dateItem);
        });

        container.appendChild(fragment);
    }

    /**
     * Setup all event listeners
     */
    _setupEventListeners() {
        this._setupScrollSync();
        this._setupInfiniteScroll();
        this._setupDatePicker();
        this._setupProgramClickHandlers();
        this._setupDragInteraction();
        this._setupResizeObserver();
    }

    /**
     * Setup resize observer for responsive updates
     */
    _setupResizeObserver() {
        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(Utils.debounce(() => {
                this._updateTimeIndicator();
                this._handleResize();
            }, 250));

            this.resizeObserver.observe(this.elements.programsGrid);
        }
    }

    /**
     * Handle window resize
     */
    _handleResize() {
        // Update any size-dependent calculations
        this._updateTimeIndicator();
    }

    /**
     * Setup scroll synchronization with improved performance
     */
    _setupScrollSync() {
        if (!this.elements.timelineScroll || !this.elements.programsGrid) {
            console.warn('Cannot setup scroll sync: missing elements');
            return;
        }

        this.elements.timelineScroll.addEventListener('scroll', this._boundMethods.handleTimelineScroll, { passive: true });
        this.elements.programsGrid.addEventListener('scroll', this._boundMethods.handleProgramsGridScroll, { passive: true });
    }

    /**
     * Handle timeline scroll events
     */
    _handleTimelineScroll() {
        this.elements.programsGrid.scrollLeft = this.elements.timelineScroll.scrollLeft;
    }

    /**
     * Handle programs grid scroll events
     */
    _handleProgramsGridScroll() {
        this.elements.timelineScroll.scrollLeft = this.elements.programsGrid.scrollLeft;
    }

    /**
     * Setup infinite scroll
     */
    _setupInfiniteScroll() {
        if (!this.elements.programsGrid) {
            console.warn('Cannot setup infinite scroll: missing programs grid element');
            return;
        }

        this.elements.programsGrid.addEventListener('scroll', this._boundMethods.handleInfiniteScroll, { passive: true });
    }

    /**
     * Handle infinite scroll events
     */
    _handleInfiniteScroll() {
        const { scrollTop, scrollHeight, clientHeight } = this.elements.programsGrid;

        if (scrollTop + clientHeight >= scrollHeight - EPG_CONFIG.INFINITE_SCROLL_THRESHOLD) {
            this.loadMoreChannels();
        }
    }

    /**
     * Setup date picker events
     */
    _setupDatePicker() {
        if (!this.elements.datePicker) {
            console.error('Date picker element not found');
            return;
        }

        this.elements.datePicker.addEventListener('click', this._boundMethods.handleDatePickerClick);
    }

    /**
     * Handle date picker click events
     */
    _handleDatePickerClick(event) {
        const dateItem = event.target.closest('.date-item');
        if (dateItem) {
            const selectedDate = dateItem.dataset.date;
            this.selectDate(selectedDate);
        }
    }

    /**
     * Setup program click handlers using event delegation
     */
    _setupProgramClickHandlers() {
        if (!this.elements.channelRows) {
            console.warn('Cannot setup program click handlers: missing channel rows element');
            return;
        }

        this.elements.channelRows.addEventListener('click', (event) => {
            if (this.dragState.hasMovedMouse || this.dragState.isDragging) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            this._handleProgramSelection(event);
        });

        this.elements.channelRows.addEventListener('touchend', (event) => {
            if (this.dragState.hasMovedMouse || this.dragState.isDragging) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            if (event.changedTouches && event.changedTouches.length === 1) {
                this._handleProgramSelection(event);
            }
        });
    }

    /**
     * Handle program selection
     */
    _handleProgramSelection(event) {
        const programCard = event.target.closest('.program-card');
        if (programCard) {
            const programId = programCard.dataset.programId;
            if (programId) {
                this.showProgramDetails(programId);
            }
        }
    }

    /**
     * Setup drag interaction
     */
    _setupDragInteraction() {
        if (!this.elements.programsGrid) {
            console.warn('Cannot setup drag interaction: missing programs grid element');
            return;
        }

        // Mouse events
        this.elements.programsGrid.addEventListener('mousedown', this._boundMethods.handleMouseDown, { passive: false });
        document.addEventListener('mousemove', this._boundMethods.handleMouseMove, { passive: false });
        document.addEventListener('mouseup', this._boundMethods.handleMouseUp, { passive: false, capture: true });

        // Touch events
        this.elements.programsGrid.addEventListener('touchstart', this._boundMethods.handleTouchStart, { passive: false });
        this.elements.programsGrid.addEventListener('touchmove', this._boundMethods.handleTouchMove, { passive: false });
        this.elements.programsGrid.addEventListener('touchend', this._boundMethods.handleTouchEnd, { passive: false });

        // Prevent context menu and text selection
        this.elements.programsGrid.addEventListener('contextmenu', (e) => {
            if (this.dragState.isDragging) e.preventDefault();
        });

        this.elements.programsGrid.style.userSelect = 'none';
        this.elements.programsGrid.style.webkitUserSelect = 'none';
    }

    /**
     * Load channels from API with improved error handling
     */
    async _loadChannels() {
        if (this.state.isLoading) return;

        this.state.isLoading = true;
        
        try {
            const response = await get('ls_get_epg_channels', {
                page: this.pagination.page,
                per_page: this.pagination.per_page,
                current_channel_id: this.channelId
            });

            if (response.success && response.data) {
                // Handle updated API response structure
                const { channels = [], pagination = {} } = response.data;

                if (this.pagination.page === 1) {
                    this.channels = channels;
                } else {
                    this.channels = [...this.channels, ...channels];
                }

                // Update pagination with API response
                this.pagination = {
                    ...this.pagination,
                    ...pagination
                };


            } else {
                console.error('Failed to load EPG channels:', response.message || 'Unknown error');
                if (this.pagination.page === 1) {
                    this.channels = [];
                }
                this.pagination.has_next = false;
            }
        } catch (error) {
            console.error('Error loading channels:', error);
            if (this.pagination.page === 1) {
                this.channels = [];
            }
            this.pagination.has_next = false;
        } finally {
            this.state.isLoading = false;
        }
    }

    /**
     * Load more channels with improved performance
     */
    async loadMoreChannels() {
        if (this.state.isLoadingMoreChannels || !this.pagination.has_next || this.state.isLoading) {
            return;
        }

        this.state.isLoadingMoreChannels = true;
        this._showLoadingIndicator();

        try {
            this.pagination.page++;
            const previousChannelCount = this.channels.length;

            await this._loadChannels();

            const newChannels = this.channels.slice(previousChannelCount);
            if (newChannels.length > 0) {
                await this._loadProgramDataForChannelsBatched(newChannels);
                this._appendNewChannelRows(newChannels);
            }
        } catch (error) {
            console.error('Error loading more channels:', error);
            this.pagination.page--;
        } finally {
            this.state.isLoadingMoreChannels = false;
            this._hideLoadingIndicator();
        }
    }

    /**
     * Show/hide loading indicators
     */
    _showLoadingIndicator() {
        if (this.elements.loadingIndicator) {
            this.elements.loadingIndicator.style.display = 'block';
        }
    }

    _hideLoadingIndicator() {
        if (this.elements.loadingIndicator) {
            this.elements.loadingIndicator.style.display = 'none';
        }
    }

    /**
     * Generate time slots
     */
    _generateTimeSlots() {
        const slots = [];
        const startTime = new Date(this.state.selectedDate);
        startTime.setHours(0, 0, 0, 0); // Start at midnight of selected date

        for (let i = 0; i < EPG_CONFIG.TIME_SLOTS_COUNT; i++) {
            const time = new Date(startTime.getTime() + (i * EPG_CONFIG.TIME_SLOT_DURATION * 60 * 1000));
            slots.push({
                time: time,
                label: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                isNow: this._isTimeSlotCurrent(time),
                hour: time.getHours(),
                minute: time.getMinutes(),
                date: time.toISOString().split('T')[0] // Add date for comparison
            });
        }

        this.timeSlots = slots;
        this._createTimeSlots();
    }

    /**
     * Check if time slot is current
     */
    _isTimeSlotCurrent(time) {
        const now = new Date();
        const slotEnd = new Date(time.getTime() + EPG_CONFIG.TIME_SLOT_DURATION * 60 * 1000);
        return now >= time && now < slotEnd;
    }

    /**
     * Create time slots using DocumentFragment for better performance
     */
    _createTimeSlots() {
        if (!this.elements.timelineScroll) {
            console.warn('Timeline scroll element not found');
            return;
        }

        const fragment = document.createDocumentFragment();

        this.timeSlots.forEach((slot, index) => {
            const isMainHour = slot.minute === 0;

            const timeSlot = document.createElement('div');
            timeSlot.className = `time-slot${slot.isNow ? ' current-time' : ''}${isMainHour ? ' main-hour' : ''}`;
            timeSlot.setAttribute('data-time', slot.time.getTime());

            const slotTime = document.createElement('div');
            slotTime.className = 'slot-time';
            slotTime.textContent = slot.label;

            timeSlot.appendChild(slotTime);
            fragment.appendChild(timeSlot);
        });

        this.elements.timelineScroll.innerHTML = '';
        this.elements.timelineScroll.appendChild(fragment);
    }

    /**
     * Load initial program data with batching for better performance
     */
    async _loadInitialProgramDataBatched() {
        try {
            if (this.channels.length === 0) {
                console.warn('No channels available to load program data');
                return;
            }

            const initialChannels = this.channels.slice(0, Math.min(this.pagination.per_page, this.channels.length));
            await this._loadProgramDataForChannelsBatched(initialChannels);
        } catch (error) {
            console.error('Error loading initial program data:', error);
        }
    }

    /**
     * Load program data for channels in batches with concurrency control
     */
    async _loadProgramDataForChannelsBatched(channels) {
        if (!channels || channels.length === 0) {
            console.warn('No channels provided to load program data');
            return;
        }
        
        this._showSkeletonLoading(channels);

        // Process channels in batches to avoid overwhelming the server
        const batchSize = EPG_CONFIG.MAX_CONCURRENT_REQUESTS;
        const batches = [];

        for (let i = 0; i < channels.length; i += batchSize) {
            batches.push(channels.slice(i, i + batchSize));
        }
       
        // Process each batch
        for (const batch of batches) {
            const promises = batch.map(channel => this._loadChannelProgramsSafe(channel.id));
            await Promise.allSettled(promises);

            // Update UI for this batch - ensure all channels are updated
            batch.forEach(channel => {
                this._updateChannelRow(channel.id);
            });
        }

        this._highlightCurrentPrograms();
    }

    /**
     * Safely load channel programs with error handling
     */
    async _loadChannelProgramsSafe(channelId) {
        try {
            // Check if already loading
            if (this.loadingChannels.has(channelId)) {
                return;
            }

            this.loadingChannels.add(channelId);
            
            await this._loadChannelPrograms(channelId);
        } catch (error) {
            console.error(`Error loading programs for channel ${channelId}:`, error);
            // Ensure channel still gets empty programs array for consistent UI
            if (!this.programData.has(channelId)) {
                this.programData.set(channelId, []);
            }
        } finally {
            this.loadingChannels.delete(channelId);
        }
    }

    /**
     * Load programs for a specific channel with caching
     */
    async _loadChannelPrograms(channelId) {
        const cacheKey = `${channelId}_${this.state.selectedDate}`;
        const cachedData = this.programCache.get(cacheKey);

        // Check cache first
        if (cachedData && Utils.isCacheValid(cachedData.timestamp)) {
            this.programData.set(channelId, cachedData.programs);
            return;
        }

        try {
            const dateToUse = this.state.selectedDate || new Date().toISOString().split('T')[0];

            const response = await get('ls_get_epg_data', {
                channel_id: channelId,
                date: dateToUse,
                timezone: this.state.userTimezone
            });

            if (response.success && response.data) {
          
                // Handle the updated API structure: channels are always returned
                const channels = response.data.channels || [];
                const channel = channels.find(ch => ch.id == channelId);
                
                if (channel) {
                    const programs = channel.programs || [];
                    const processedPrograms = this._processApiPrograms(programs, channelId);
                    this.programData.set(channelId, processedPrograms);

                    // Cache the data
                    this.programCache.set(cacheKey, {
                        programs: processedPrograms,
                        timestamp: Date.now()
                    });
                } else {
                    // Fallback: check for legacy API response format
                    if (response.data.programs && Array.isArray(response.data.programs)) {
                        const processedPrograms = this._processApiPrograms(response.data.programs, channelId);
                        this.programData.set(channelId, processedPrograms);

                        this.programCache.set(cacheKey, {
                            programs: processedPrograms,
                            timestamp: Date.now()
                        });
                    } else {
                        // Channel not found in response, set empty programs
                        this.programData.set(channelId, []);
                        this.programCache.set(cacheKey, {
                            programs: [],
                            timestamp: Date.now()
                        });
                    }
                }
            } else {
                console.warn(`API error loading programs for channel ${channelId}:`, response.message || 'Unknown error');
                this.programData.set(channelId, []);
                this.programCache.set(cacheKey, {
                    programs: [],
                    timestamp: Date.now()
                });
            }
        } catch (error) {
            console.error(`Error loading programs for channel ${channelId}:`, error);
            this.programData.set(channelId, []);
            // Cache empty data to avoid repeated failed requests
            this.programCache.set(cacheKey, {
                programs: [],
                timestamp: Date.now()
            });
        }
    }

    /**
     * Process API program data with validation
     * Note: Backend handles all timezone conversions and date filtering
     */
    _processApiPrograms(apiPrograms, channelId) {

        if (!Array.isArray(apiPrograms)) {
            console.warn(`Invalid programs data for channel ${channelId}`);
            return [];
        }

        // Only validate data structure - backend already handles timezone and date filtering
        return apiPrograms
            .filter(program => program && program.start_time && program.end_time)
            .map(program => ({
                id: program.id || `prog_${channelId}_${Date.now()}_${Math.random()}`,
                title: program.title || 'Unknown Program',
                description: program.description || 'No description available',
                start_time: program.start_time,
                end_time: program.end_time,
                thumbnail: program.thumbnail || 'https://via.placeholder.com/50x50/444/fff?text=?',
                category: program.category || 'General',
                background_image: program.background_image || null,
                // Preserve any timezone-related fields from backend
                user_start_time: program.user_start_time,
                user_end_time: program.user_end_time,
                user_timezone: program.user_timezone,
                user_timezone_abbr: program.user_timezone_abbr,
                start_time_formatted: program.start_time_formatted,
                end_time_formatted: program.end_time_formatted,
                date_formatted: program.date_formatted,
                spans_multiple_days: program.spans_multiple_days,
                is_current: program.is_current
            }))
            .sort((a, b) => a.start_time - b.start_time);
    }

    /**
     * Show skeleton loading with better performance
     */
    _showSkeletonLoading(channels) {
        if (!this.elements.channelRows) return;

        const fragment = document.createDocumentFragment();

        channels.forEach(channel => {
            const channelRow = this.elements.channelRows.querySelector(`[data-channel-id="${channel.id}"]`);
            if (channelRow) {
                const programsRow = channelRow.querySelector('.programs-row');
                if (programsRow) {
                    programsRow.innerHTML = this._createProgramsSkeletonHTML();
                    channelRow.classList.add('skeleton-loading');
                }
            }
        });
    }

    /**
     * Create skeleton HTML for programs
     */
    _createProgramsSkeletonHTML() {
        let skeletonHTML = '';
        for (let i = 0; i < 45; i++) {
            skeletonHTML += `
                <div class="program-slot" style="left: ${i * 250}px; width: 250px;">
                    <div class="program-card skeleton">
                        <div class="program-inner">
                            <div class="program-image skeleton"></div>
                            <div class="program-info">
                                <div class="program-title skeleton"></div>
                                <div class="program-time skeleton"></div>
                                <div class="program-category skeleton"></div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
        }
        return skeletonHTML;
    }

    /**
     * Update channel row with loaded data
     */
    _updateChannelRow(channelId, isError = false) {
        if (!this.elements.channelRows) return;

        const channelRow = this.elements.channelRows.querySelector(`[data-channel-id="${channelId}"]`);
        if (!channelRow) {
            console.warn(`Channel row not found for channel ID: ${channelId}`);
            return;
        }

        channelRow.classList.remove('skeleton-loading');

        const channel = this.channels.find(c => c.id == channelId);
        if (!channel) {
            console.warn(`Channel not found for ID: ${channelId}`);
            return;
        }

        const programs = this.programData.get(channelId) || [];
        const programsRow = channelRow.querySelector('.programs-row');
        if (!programsRow) {
            console.warn(`Programs row not found for channel ID: ${channelId}`);
            return;
        }

        // Always clear the programs row first
        programsRow.innerHTML = '';

        if (isError) {
            programsRow.innerHTML = `
                <div class="program-slot" style="width: 100%;">
                    <div class="program-card error">
                        <div class="program-inner">
                            <div class="program-info">
                                <div class="program-title">Error Loading Programs</div>
                                <div class="program-time">Please try again later</div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            return;
        }

        // Process programs for the selected date
        const processedPrograms = this._processChannelPrograms(programs);
        
       
        if (processedPrograms.length === 0) {
           
            programsRow.innerHTML = `
                <div class="program-slot" style="width: 100%;">
                    <div class="program-card no-program">
                        <div class="program-inner">
                            <div class="program-info">
                                <div class="program-title">No Programs Available</div>
                                <div class="program-time">Check back later for updates</div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            return;
        }

        // Build programs with DocumentFragment
        const fragment = document.createDocumentFragment();
        processedPrograms.forEach(program => {
            const programSlot = this._createProgramSlotDOM(program);
            fragment.appendChild(programSlot);
        });

        programsRow.appendChild(fragment);
    }

    /**
     * Process channel programs to handle overlaps
     * Note: Backend already filters programs by date and timezone, so we trust the API response
     */
    _processChannelPrograms(programs) {
        if (!Array.isArray(programs)) return [];

        // Only validate basic program structure - NO date filtering since backend handles it
        const validPrograms = programs
            .filter(program => {
                return program && 
                    program.start_time && 
                    program.end_time &&
                    typeof program.start_time === 'number' &&
                    typeof program.end_time === 'number';
            })
            .sort((a, b) => a.start_time - b.start_time);

        return this._resolveOverlappingPrograms(validPrograms);
    }

    /**
     * Create program slot DOM element with optimizations
     */
    _createProgramSlotDOM(program) {
        const programWidth = this._calculateProgramWidth(program);
        const programPosition = this._calculateProgramPosition(program);
        
        // Use backend-formatted times if available, otherwise fallback to client-side formatting
        let timeLabel;
        if (program.start_time_formatted && program.end_time_formatted) {
            // Backend provided formatted times with timezone (e.g., "2:30 PM IST - 4:00 PM IST")
            timeLabel = `${program.start_time_formatted} - ${program.end_time_formatted}`;
        } else if (program.user_start_time && program.user_end_time) {
            // Backend provided user timezone times
            timeLabel = `${program.user_start_time} - ${program.user_end_time}`;
            if (program.user_timezone_abbr) {
                timeLabel += ` ${program.user_timezone_abbr}`;
            }
        } else {
            // Fallback to client-side formatting
            const startTime = new Date(program.start_time * 1000);
            const endTime = new Date(program.end_time * 1000);
            timeLabel = `${startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        }

        const programSlot = document.createElement('div');
        programSlot.className = 'program-slot';
        programSlot.style.cssText = `position: absolute; left: ${programPosition}px; width: ${programWidth}px;`;
        programSlot.setAttribute('data-program-id', program.id);

        const programCard = document.createElement('div');
        // Use backend current program flag if available
        const isCurrent = program.is_current !== undefined ? program.is_current : this._isCurrentProgram(program);
        programCard.className = `program-card ${isCurrent ? 'current-program' : ''}`;
        programCard.setAttribute('data-program-id', program.id);

        if (program.background_image) {
            programCard.style.cssText = `
                background-image: linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${program.background_image});
                background-size: cover;
                background-position: center;
            `;
        }

        const programInner = document.createElement('div');
        programInner.className = 'program-inner';

        let imageHTML = '';
        if (!program.background_image) {
            imageHTML = `<img class="program-image" src="${program.thumbnail}" alt="${program.title}" onerror="this.src='https://via.placeholder.com/50x30/444/fff?text=${program.category.charAt(0)}'">`;
        }

        programInner.innerHTML = `
            ${imageHTML}
            <div class="program-info">
                <div class="program-title" title="${program.title}">${program.title}</div>
                <div class="program-time">${timeLabel}</div>
                <div class="program-category">${program.category}</div>
            </div>
        `;

        programCard.appendChild(programInner);
        programSlot.appendChild(programCard);

        return programSlot;
    }

    /**
     * Calculate program width and position
     */
    _calculateProgramWidth(program) {
        const durationInMinutes = (program.end_time - program.start_time) / 60;
        return (durationInMinutes / EPG_CONFIG.TIME_SLOT_DURATION) * EPG_CONFIG.SLOT_WIDTH;
    }

    _calculateProgramPosition(program) {
        const programDate = new Date(program.start_time * 1000);
        
        // Since backend already filters programs by date and timezone, we trust it
        // Calculate position based on the program's start time relative to midnight of selected date
        const midnight = new Date(this.state.selectedDate);
        midnight.setHours(0, 0, 0, 0);

        const minutesFromMidnight = (programDate - midnight) / (1000 * 60);
        return (minutesFromMidnight / EPG_CONFIG.TIME_SLOT_DURATION) * EPG_CONFIG.SLOT_WIDTH;
    }

    /**
     * Check if program is currently airing
     */
    _isCurrentProgram(program) {
        const now = Date.now() / 1000;
        const programDate = new Date(program.start_time * 1000).toISOString().split('T')[0];
        const today = new Date().toISOString().split('T')[0];

        // Only highlight as current if it's today's date
        return programDate === today &&
            now >= program.start_time &&
            now < program.end_time;
    }

    /**
     * Highlight current programs with batch processing
     */
    _highlightCurrentPrograms() {
        if (!this.elements.channelRows) return;

        // Remove existing highlights in batch
        const currentPrograms = this.elements.channelRows.querySelectorAll('.program-card.current-program');
        currentPrograms.forEach(card => card.classList.remove('current-program'));

        // Add new highlights
        const fragment = document.createDocumentFragment();
        for (const programs of this.programData.values()) {
            programs.forEach(program => {
                if (this._isCurrentProgram(program)) {
                    const programElement = this.elements.channelRows?.querySelector(`[data-program-id="${program.id}"] .program-card`);
                    if (programElement) {
                        programElement.classList.add('current-program');
                    }
                }
            });
        }
    }

    /**
     * Render EPG with performance optimizations
     */
    _renderEPG(skeletonMode = false) {
        if (!this.elements.channelRows) {
            console.warn('Channel rows element not found');
            return;
        }

        if (this.channels.length === 0) {
            this.elements.channelRows.innerHTML = '<div class="loading">No channels available</div>';
            return;
        }

        // Use DocumentFragment for batch DOM updates
        const fragment = document.createDocumentFragment();

        this.channels.forEach(channel => {
            const channelRowElement = this._createChannelRowDOM(channel, skeletonMode);
            fragment.appendChild(channelRowElement);
        });

        this.elements.channelRows.innerHTML = '';
        this.elements.channelRows.appendChild(fragment);

        if (!skeletonMode) {
            this._highlightCurrentPrograms();
        }
    }

    /**
     * Create channel row DOM with improved performance
     */
    _createChannelRowDOM(channel, skeletonMode = false) {
        const programs = skeletonMode ? [] : (this.programData.get(channel.id) || []);

        const channelRow = document.createElement('div');
        channelRow.className = `channel-row ${skeletonMode ? 'skeleton-loading' : ''}`;
        channelRow.setAttribute('data-channel-id', channel.id);

        // Channel item
        const channelItem = document.createElement('div');
        channelItem.className = 'channel-item';

        const channelContent = document.createElement('div');
        channelContent.className = 'channel-content';


        channelContent.innerHTML = `
                <img class="channel-logo" src="${channel.channel_logo}" alt="${channel.name}" onerror="this.src='https://via.placeholder.com/80x50/333/fff?text=${channel.name.charAt(0)}'">
                <div class="channel-name">${channel.name}</div>
            `;
            
        channelItem.appendChild(channelContent);

        // Programs row
        const programsRow = document.createElement('div');
        programsRow.className = 'programs-row';

        if (skeletonMode) {
            programsRow.innerHTML = this._createProgramsSkeletonHTML();
        } else {
            const processedPrograms = this._processChannelPrograms(programs);
            
            if (processedPrograms.length === 0) {
                programsRow.innerHTML = `
                    <div class="program-slot" style="width: 100%;">
                        <div class="program-card no-program">
                            <div class="program-inner">
                                <div class="program-info">
                                    <div class="program-title">No Programs Available</div>
                                    <div class="program-time">Check back later for updates</div>
                                </div>
                            </div>
                        </div>
                    </div>
                `;
            } else {
                const fragment = document.createDocumentFragment();

                processedPrograms.forEach(program => {
                    const programSlot = this._createProgramSlotDOM(program);
                    fragment.appendChild(programSlot);
                });

                programsRow.appendChild(fragment);
            }
        }

        channelRow.appendChild(channelItem);
        channelRow.appendChild(programsRow);

        return channelRow;
    }

    /**
     * Append new channel rows for infinite scroll
     */
    _appendNewChannelRows(newChannels) {
        if (!this.elements.channelRows || !newChannels.length) {
            return;
        }

        const fragment = document.createDocumentFragment();

        newChannels.forEach(channel => {
            const channelRowElement = this._createChannelRowDOM(channel);
            fragment.appendChild(channelRowElement);
        });

        this.elements.channelRows.appendChild(fragment);
        this._highlightCurrentPrograms();
    }

    /**
     * Update time indicator with improved performance
     */
    _updateTimeIndicator() {
        if (!this.state.timeIndicatorVisible || !this.elements.timeIndicator) return;

        const now = new Date();
        const today = new Date().toISOString().split('T')[0];

        // Only show indicator if viewing today's programs
        if (this.state.selectedDate !== today) {
            this.elements.timeIndicator.style.display = 'none';
            return;
        }

        this.elements.timeIndicator.style.display = 'block';

        const midnight = new Date(today);
        midnight.setHours(0, 0, 0, 0);
        const minutesSinceMidnight = (now - midnight) / (1000 * 60);
        const position = EPG_CONFIG.CHANNEL_WIDTH +
            (minutesSinceMidnight / EPG_CONFIG.TIME_SLOT_DURATION) * EPG_CONFIG.SLOT_WIDTH;

        this.elements.timeIndicator.style.left = `${position}px`;
    }

    /**
     * Start time updater with better performance
     */
    _startTimeUpdater() {
        this.intervals.timeUpdater = setInterval(this._boundMethods.updateTime, EPG_CONFIG.UPDATE_INTERVAL);
    }

    /**
     * Update time
     */
    _updateTime() {
        this.state.currentTime = new Date();
        this._updateTimeIndicator();
        this._highlightCurrentPrograms();
    }

    // Drag interaction methods (optimized)
    _handleMouseDown(event) {
        if (event.button !== 0) return;
        if (this._isInteractiveElement(event.target)) return;

        this._startDrag(event.clientX, event.clientY);
        event.preventDefault();
    }

    _handleMouseMove(event) {
        if (!this.dragState.isDragging) return;
        this._updateDrag(event.clientX);
        event.preventDefault();
    }

    _handleMouseUp(event) {
        if (!this.dragState.isDragging) return;
        this._endDrag();
        event.preventDefault();
    }

    _handleTouchStart(event) {
        if (event.touches.length > 1) return;
        const touch = event.touches[0];
        if (this._isInteractiveElement(event.target)) return;

        this._startDrag(touch.clientX, touch.clientY);
        event.preventDefault();
    }

    _handleTouchMove(event) {
        if (!this.dragState.isDragging || event.touches.length > 1) return;
        const touch = event.touches[0];
        this._updateDrag(touch.clientX);
        event.preventDefault();
    }

    _handleTouchEnd(event) {
        if (!this.dragState.isDragging) return;
        this._endDrag();
        event.preventDefault();
    }

    _isInteractiveElement(element) {
        return (element.matches && element.matches('.product-card')) ||
            (element.closest && element.closest('.product-card'));
    }

    _startDrag(clientX, clientY) {
        this.dragState.isDragging = true;
        this.dragState.startX = clientX;
        this.dragState.startScrollLeft = this.elements.programsGrid.scrollLeft;
        this.dragState.hasMovedMouse = false;
        this.dragState.velocity = 0;
        this.dragState.lastMoveTime = Date.now();
        this.dragState.lastMoveX = clientX;

        if (this.dragState.momentumAnimation) {
            cancelAnimationFrame(this.dragState.momentumAnimation);
            this.dragState.momentumAnimation = null;
        }

        this.elements.programsGrid.classList.add('epg-dragging');
        document.body.style.cursor = 'grabbing';
    }

    _updateDrag(clientX) {
        const deltaX = this.dragState.startX - clientX;
        const newScrollLeft = this.dragState.startScrollLeft + deltaX;

        if (!this.dragState.hasMovedMouse && Math.abs(deltaX) > this.dragState.dragThreshold) {
            this.dragState.hasMovedMouse = true;
        }

        const currentTime = Date.now();
        const timeDelta = currentTime - this.dragState.lastMoveTime;

        if (timeDelta > 0) {
            const moveDelta = clientX - this.dragState.lastMoveX;
            this.dragState.velocity = moveDelta / timeDelta;
            this.dragState.lastMoveTime = currentTime;
            this.dragState.lastMoveX = clientX;
        }

        this.elements.programsGrid.scrollLeft = Math.max(0, newScrollLeft);
    }

    _endDrag() {
        const wasDragging = this.dragState.hasMovedMouse;

        this.elements.programsGrid.classList.remove('epg-dragging');
        document.body.style.cursor = '';

        if (wasDragging && Math.abs(this.dragState.velocity) > 0.1) {
            this._applyMomentumScrolling();
        }

        this.dragState.isDragging = false;
        this.dragState.velocity = 0;

        if (wasDragging) {
            setTimeout(() => {
                this.dragState.hasMovedMouse = false;
            }, 150);
        } else {
            this.dragState.hasMovedMouse = false;
        }
    }

    _applyMomentumScrolling() {
        const friction = 0.95;
        const minVelocity = 0.1;

        const animate = () => {
            if (Math.abs(this.dragState.velocity) < minVelocity) {
                this.dragState.momentumAnimation = null;
                return;
            }

            const currentScrollLeft = this.elements.programsGrid.scrollLeft;
            const newScrollLeft = currentScrollLeft - (this.dragState.velocity * 16);

            const maxScrollLeft = this.elements.programsGrid.scrollWidth - this.elements.programsGrid.clientWidth;
            const clampedScrollLeft = Math.max(0, Math.min(maxScrollLeft, newScrollLeft));

            this.elements.programsGrid.scrollLeft = clampedScrollLeft;
            this.dragState.velocity *= friction;

            if ((clampedScrollLeft <= 0 && this.dragState.velocity > 0) ||
                (clampedScrollLeft >= maxScrollLeft && this.dragState.velocity < 0)) {
                this.dragState.velocity = 0;
                this.dragState.momentumAnimation = null;
                return;
            }

            this.dragState.momentumAnimation = requestAnimationFrame(animate);
        };

        this.dragState.momentumAnimation = requestAnimationFrame(animate);
    }

    /**
     * Navigate to current time
     */
    _goToCurrentTime() {
        if (!this.elements.programsGrid) return;

        const today = new Date().toISOString().split('T')[0];
        if (this.state.selectedDate !== today) return;

        try {
            const currentTimeSlot = this.elements.timelineScroll?.querySelector('.current-time');

            if (currentTimeSlot) {
                const currentTimeLeft = currentTimeSlot.offsetLeft;
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                const scrollPosition = currentTimeLeft - (viewportWidth / 2);

                this.elements.programsGrid.scrollTo({
                    left: Math.max(0, scrollPosition)
                });

                if (this.elements.timelineScroll) {
                    this.elements.timelineScroll.scrollLeft = this.elements.programsGrid.scrollLeft;
                }
            } else {
                const now = new Date();
                const midnight = new Date();
                midnight.setHours(0, 0, 0, 0);

                const minutesSinceMidnight = (now - midnight) / (1000 * 60);
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                const scrollPosition = (minutesSinceMidnight / EPG_CONFIG.TIME_SLOT_DURATION) * EPG_CONFIG.SLOT_WIDTH - (viewportWidth / 2);

                this._updateTimeIndicator();

                this.elements.programsGrid.scrollTo({
                    left: Math.max(0, scrollPosition)
                });

                if (this.elements.timelineScroll) {
                    this.elements.timelineScroll.scrollLeft = this.elements.programsGrid.scrollLeft;
                }
            }
        } catch (error) {
            console.error('Error focusing on current time:', error);
        }
    }

    /**
     * Focus on live time with retry
     */
    _focusOnLiveTime() {
        const attemptFocus = (retryCount = 0) => {
            setTimeout(() => {
                if (this.elements.programsGrid) {
                    this._goToCurrentTime();

                    const currentTimeSlot = this.elements.timelineScroll?.querySelector('.current-time');
                    if (!currentTimeSlot && retryCount < EPG_CONFIG.FOCUS_RETRY_COUNT) {
                        attemptFocus(retryCount + 1);
                    }
                } else if (retryCount < EPG_CONFIG.FOCUS_RETRY_COUNT) {
                    attemptFocus(retryCount + 1);
                }
            }, retryCount === 0 ? EPG_CONFIG.FOCUS_INITIAL_DELAY : EPG_CONFIG.FOCUS_RETRY_DELAY);
        };

        attemptFocus();
    }

    /**
     * Select date with improved error handling
     */
    async selectDate(date) {
        if (date === this.state.selectedDate) return;

        // Update UI immediately for better UX
        this.elements.datePicker.querySelectorAll('.date-item').forEach(item => {
            item.classList.remove('active');
            if (item.dataset.date === date) {
                item.classList.add('active');
            }
        });

        const success = await this.changeDate(date);
        if (!success) {
            console.error('Failed to change date, reverting selection');
            // Revert UI
            this.elements.datePicker.querySelectorAll('.date-item').forEach(item => {
                item.classList.remove('active');
                if (item.dataset.date === this.state.selectedDate) {
                    item.classList.add('active');
                }
            });
        }
    }

    /**
     * Change date with improved performance
     */
    async changeDate(date) {
        if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            console.error('Invalid date format. Expected format: YYYY-MM-DD');
            return false;
        }

        this.state.isLoading = true;

        try {
            this.state.selectedDate = date;

            // Clear cache for old date
            for (const [key] of this.programCache) {
                if (!key.includes(date)) {
                    this.programCache.delete(key);
                }
            }

            this.programData.clear();

            if (this.elements.channelRows) {
                this.elements.channelRows.classList.add('loading');
            }

            // Regenerate time slots for the new date
            this._generateTimeSlots();
            
            const initialChannels = this.channels.slice(0, Math.min(this.pagination.per_page, this.channels.length));
            await this._loadProgramDataForChannelsBatched(initialChannels);

            // Re-render EPG with new date and data
            this._renderEPG();
            
            // Ensure all visible channels are updated, even those with no programs
            this.channels.forEach(channel => {
                this._updateChannelRow(channel.id);
            });
            
            this._updateTimeIndicator();

            const today = new Date().toISOString().split('T')[0];
            if (date === today) {
                this._focusOnLiveTime();
            } else {
                if (this.elements.programsGrid) {
                    this.elements.programsGrid.scrollLeft = 0;
                }
            }

            return true;
        } catch (error) {
            console.error('Error changing EPG date:', error);
            return false;
        } finally {
            this.state.isLoading = false;
            if (this.elements.channelRows) {
                this.elements.channelRows.classList.remove('loading');
            }
        }
    }

    /**
     * Refresh data with improved performance
     */
    async refreshData() {
        this.state.isLoading = true;

        try {
            // Reset pagination
            this.pagination.page = 1;
            this.pagination.has_next = true;
            this.state.isLoadingMoreChannels = false;

            // Clear all data and cache
            this.channels = [];
            this.programData.clear();
            this.programCache.clear();

            await this._loadChannels();

            if (this.channelId) {
                const channel = this.channels.find(c => c.id == this.channelId);
                if (channel) {
                    await this._loadChannelProgramsSafe(this.channelId);
                }
            }

            await this._loadInitialProgramDataBatched();
            this._renderEPG();
            this._updateTimeIndicator();
            this._focusOnLiveTime();

            return true;
        } catch (error) {
            console.error('Error refreshing EPG data:', error);
            return false;
        } finally {
            this.state.isLoading = false;
        }
    }

    /**
     * Optionally prefetch program data for adjacent dates using the multi-day API
     * This is a performance enhancement that can be called after initial load
     */
    async prefetchAdjacentDates() {
        try {
            // Get yesterday, today, and tomorrow
            const today = new Date(this.state.selectedDate);
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            const tomorrow = new Date(today);
            tomorrow.setDate(today.getDate() + 1);

            const dates = [
                yesterday.toISOString().split('T')[0],
                this.state.selectedDate,
                tomorrow.toISOString().split('T')[0]
            ];

            // Use the multi-day API to fetch data for all dates at once
            const response = await get('ls_get_epg_multi_day', {
                channel_ids: this.channels.slice(0, Math.min(5, this.channels.length)).map(c => c.id), // Limit to first 5 channels to avoid large requests
                dates: dates
            });

            if (response.success && response.data && response.data.program_data) {
                // Cache the program data for each channel and date
                Object.entries(response.data.program_data).forEach(([date, channels]) => {
                    if (Array.isArray(channels)) {
                        channels.forEach(channel => {
                            if (channel.programs && Array.isArray(channel.programs)) {
                                const cacheKey = `${channel.id}_${date}`;
                                const processedPrograms = this._processApiPrograms(channel.programs, channel.id);
                                
                                this.programCache.set(cacheKey, {
                                    programs: processedPrograms,
                                    timestamp: Date.now()
                                });

                                // If this is for the current date, update the program data
                                if (date === this.state.selectedDate) {
                                    this.programData.set(channel.id, processedPrograms);
                                }
                            }
                        });
                    }
                });

                console.log('Successfully prefetched EPG data for adjacent dates');
                return true;
            }
        } catch (error) {
            console.warn('Could not prefetch adjacent dates (falling back to single-day requests):', error);
        }
        
        return false;
    }

    /**
     * Set user timezone and refresh data if needed
     * @param {string} timezone - Valid timezone identifier (e.g., 'America/New_York')
     */
    async setTimezone(timezone) {
        if (!timezone || typeof timezone !== 'string') {
            console.error('Invalid timezone provided');
            return false;
        }

        // Validate timezone
        try {
            Intl.DateTimeFormat(undefined, { timeZone: timezone });
        } catch (error) {
            console.error('Invalid timezone:', timezone);
            return false;
        }

        const oldTimezone = this.state.userTimezone;
        this.state.userTimezone = timezone;

        // Clear cache as timezone changed
        this.programCache.clear();
        this.programData.clear();

        // If timezone actually changed, refresh data
        if (oldTimezone !== timezone) {
            try {
                await this.refreshData();
                console.log(`EPG timezone updated to: ${timezone}`);
                return true;
            } catch (error) {
                console.error('Error refreshing data after timezone change:', error);
                // Revert timezone on failure
                this.state.userTimezone = oldTimezone;
                return false;
            }
        }

        return true;
    }

    /**
     * Get current user timezone
     */
    getTimezone() {
        return this.state.userTimezone;
    }

    /**
     * Get user's timezone offset for consistent time handling
     */
    _getUserTimezoneOffset() {
        return new Date().getTimezoneOffset();
    }

    /**
     * Convert UTC timestamp to user's local time
     */
    _convertToUserTime(utcTimestamp) {
        // The backend now handles timezone conversion, but this method
        // can be used for any additional client-side time processing
        return utcTimestamp;
    }

    /**
     * Check if date needs timezone adjustment
     */
    _needsTimezoneAdjustment(programDate, selectedDate) {
        // For now, we rely on the backend timezone handling
        // This method is kept for future timezone-related enhancements
        return false;
    }

    /**
     * Resolve overlapping programs
     */
    _resolveOverlappingPrograms(programs) {
        // Keep all programs for now, but could implement smarter resolution
        return programs;
    }

    /**
     * Check if programs overlap
     */
    _programsOverlap(program1, program2) {
        return (program1.start_time < program2.end_time && program2.start_time < program1.end_time);
    }

    /**
     * Public API methods
     */
    getCurrentProgram(channelId) {
        const programs = this.programData.get(channelId) || [];
        const now = Date.now() / 1000;

        return programs.find(program =>
            now >= program.start_time && now < program.end_time
        ) || null;
    }

    getNextProgram(channelId) {
        const programs = this.programData.get(channelId) || [];
        const now = Date.now() / 1000;

        const futurePrograms = programs
            .filter(program => program.start_time > now)
            .sort((a, b) => a.start_time - b.start_time);

        return futurePrograms[0] || null;
    }

    getChannels() {
        return this.channels;
    }

    getChannelPrograms(channelId) {
        return this.programData.get(channelId) || [];
    }

    goToCurrentTime() {
        this._goToCurrentTime();
    }

    /**
     * Show program details with enhanced features
     */
    showProgramDetails(programId) {
        let programData = null;

        for (const programs of this.programData.values()) {
            const foundProgram = programs.find(program => program.id === programId);
            if (foundProgram) {
                programData = foundProgram;
                break;
            }
        }

        if (!programData) {
            console.warn('Program not found:', programId);
            return;
        }

        const startTime = new Date(programData.start_time * 1000);
        const endTime = new Date(programData.end_time * 1000);
        const formattedDate = startTime.toLocaleDateString();
        const formattedTime = `${startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

        let isLive = false;
        let channelLink = null;

        try {
            const now = Date.now() / 1000;
            isLive = now >= programData.start_time && now < programData.end_time;

            // Find the channel for this program
            for (const channel of this.channels) {
                const channelPrograms = this.programData.get(channel.id) || [];
                if (channelPrograms.some(p => p.id === programId)) {
                    channelLink = channel.chanel_link || null;
                    break;
                }
            }
        } catch (error) {
            console.warn('Error checking program status:', error);
        }
        

        // Show program details modal
        const modalContent = `
            <div class="epg-program-details program-details-popup">
                <div class="program-details-header">
                    <img src="${programData.thumbnail}" alt="${programData.title}" class="program-details-thumbnail" onerror="this.src='https://via.placeholder.com/100x100/444/fff?text=${programData.category.charAt(0)}'">
                    <div class="program-details-info">
                        <h5 class="program-details-title">${programData.title}</h5>
                        <div class="program-details-meta">
                            <span class="program-details-category">${programData.category}</span>
                            <span class="program-details-date">${formattedDate}</span>
                            <span class="program-details-time">${formattedTime}</span>
                        </div>
                    </div>
                </div>
                <div class="program-details-description">
                    <p>${programData.description}</p>
                </div>
                ${isLive && channelLink ? `
                <div class="program-details-actions">
                    <a class="btn btn-primary goto-live-btn" href="${channelLink}">
                        <i class="fas fa-play"></i> Watch Now
                    </a>
                </div>
                ` : ''}
            </div>
        `;

        Swal.fire({
            html: modalContent,
            showCloseButton: true,
            showConfirmButton: false,
            width: "600px",
            backdrop: "rgba(0,0,0,0.5)",
            customClass: {
                popup: 'program-details-modal',
                closeButton: 'program-details-close'
            }
        });
    }

    /**
     * Destroy EPG instance and cleanup
     */
    destroy() {
        // Clear intervals
        if (this.intervals.timeUpdater) {
            clearInterval(this.intervals.timeUpdater);
            this.intervals.timeUpdater = null;
        }

        // Clear momentum animation
        if (this.dragState.momentumAnimation) {
            cancelAnimationFrame(this.dragState.momentumAnimation);
            this.dragState.momentumAnimation = null;
        }

        // Disconnect resize observer
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        // Remove event listeners
        Object.values(this._boundMethods).forEach(method => {
            // Event listeners will be removed when elements are destroyed
        });

        // Clear request queue
        this.requestQueue.clear();
        this.loadingChannels.clear();

        // Reset state
        this.pagination = {
            page: 1,
            per_page: EPG_CONFIG.CHANNELS_PER_PAGE,
            total: 0,
            total_pages: 0,
            has_next: false,
            has_prev: false
        };

        this.state.isLoadingMoreChannels = false;
        this.state.isLoading = false;
        this.state.initialized = false;

        // Clear data and cache
        this.channels = [];
        this.programData.clear();
        this.programCache.clear();
        this.timeSlots = [];

        // Clear DOM
        const container = document.getElementById(this.containerId);
        if (container) {
            container.innerHTML = '';
        }

        // Clear element references
        Object.keys(this.elements).forEach(key => {
            this.elements[key] = null;
        });
    }
}

// Global export for compatibility
window.EPGManager = EPGManager;

export default EPGManager;